import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { setupApiInterceptors } from '../services/api'
import { userService } from '../services/userService'
import { petService } from '../services/petService'
import { User } from '../types/User'
import LoadingSpinner from '../components/Common/LoadingSpinner'

export default function Profile() {
  const { user: auth0User, getAccessTokenSilently } = useAuth0()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    address: ''
  })

  useEffect(() => {
    setupApiInterceptors(getAccessTokenSilently)
    loadProfile()
  }, [getAccessTokenSilently])

  const loadProfile = async () => {
    try {
      const data = await userService.getCurrentUser()
      setUser(data)
      setFormData({
        name: data.name || '',
        phoneNumber: data.phoneNumber || '',
        address: data.address || ''
      })
    } catch (error) {
      console.error('Failed to load profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const updated = await userService.updateProfile(formData)
      setUser(updated)
      setEditing(false)
    } catch (error) {
      console.error('Failed to update profile:', error)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Profile</h1>

      <div className="card mb-6">
        <h2 className="text-xl font-semibold mb-4">Account Information</h2>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-gray-600">Email</label>
            <p className="font-medium">{auth0User?.email}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Contact Information</h2>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="btn-primary"
            >
              Edit
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="input-field"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary">
                Save Changes
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Full Name</label>
              <p className="font-medium">{user?.name || 'Not set'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Phone Number</label>
              <p className="font-medium">{user?.phoneNumber || 'Not set'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Address</label>
              <p className="font-medium">{user?.address || 'Not set'}</p>
            </div>
          </div>
        )}
      </div>

      <NotificationPreferencesCard />
    </div>
  )
}

function NotificationPreferencesCard() {
  const [prefs, setPrefs] = useState({ tagScanned: true, lostMode: true, tagRegistered: true })
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    petService.getNotificationPreferences()
      .then(p => { setPrefs(p); setLoaded(true) })
      .catch(console.error)
  }, [])

  const toggle = async (key: keyof typeof prefs) => {
    const updated = { ...prefs, [key]: !prefs[key] }
    setPrefs(updated)
    await petService.updateNotificationPreferences(updated)
  }

  if (!loaded) return null

  return (
    <div className="card mt-6">
      <h2 className="text-xl font-semibold mb-2">Notification Preferences</h2>
      <p className="text-sm text-gray-500 mb-4">Choose which events you want to be notified about</p>
      <div className="space-y-3">
        <label className="flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-700 font-medium">Tag Scanned</span>
            <p className="text-xs text-gray-500">When someone scans your lost pet's tag</p>
          </div>
          <input type="checkbox" checked={prefs.tagScanned} onChange={() => toggle('tagScanned')} className="rounded text-primary-500 focus:ring-primary-400" />
        </label>
        <label className="flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-700 font-medium">Lost Mode</span>
            <p className="text-xs text-gray-500">When lost mode is activated or deactivated</p>
          </div>
          <input type="checkbox" checked={prefs.lostMode} onChange={() => toggle('lostMode')} className="rounded text-primary-500 focus:ring-primary-400" />
        </label>
        <label className="flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-700 font-medium">Tag Registered</span>
            <p className="text-xs text-gray-500">When a tag is linked to your pet</p>
          </div>
          <input type="checkbox" checked={prefs.tagRegistered} onChange={() => toggle('tagRegistered')} className="rounded text-primary-500 focus:ring-primary-400" />
        </label>
      </div>
    </div>
  )
}
